import React, { useEffect, useState } from "react";
import { Add, Delete, Edit, Send } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SenderSignatureEditor from "./SenderSignatureEditor";
import ConfirmationDialog from "./ConfirmationDialog";
import { parseStructuredDocument } from "./structuredDocument";

const emptyForm = {
  name: "",
  displayName: "",
  fromEmail: "",
  replyToEmail: "",
  smtpUsername: "",
  smtpPassword: "",
  isActive: true,
  dailySendLimit: 30,
  allocations: [],
  signatureDocument: {},
  signatureImageUrl: null,
  signatureImage: null,
  signatureImageWidth: 320,
  signatureImageLinkUrl: "",
  removeSignatureImage: false,
};

export default function SenderProfilesSection({
  api,
  senders,
  workflows,
  onChanged,
}) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [testSender, setTestSender] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const [senderToDelete, setSenderToDelete] = useState(null);
  const [signatureImageUpdating, setSignatureImageUpdating] = useState(false);
  const [localSignatureImageUrl, setLocalSignatureImageUrl] = useState(null);

  useEffect(() => {
    if (!form.signatureImage) {
      setLocalSignatureImageUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(form.signatureImage);
    setLocalSignatureImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.signatureImage]);

  useEffect(() => {
    if (editing) {
      setForm({
        ...emptyForm,
        name: editing.name,
        displayName: editing.displayName,
        fromEmail: editing.fromEmail,
        replyToEmail: editing.replyToEmail,
        isActive: editing.isActive,
        dailySendLimit: editing.dailySendLimit || 30,
        allocations: (editing.allocations || []).map((allocation) => ({
          workflow: (workflows || []).find(
            (workflow) => workflow.id === allocation.workflow?.id,
          ) || {
            ...allocation.workflow,
            name: allocation.workflow?.name || allocation.workflowName,
            workflowTypeLabel:
              allocation.workflow?.workflowTypeLabel ||
              allocation.workflowTypeLabel,
          },
          percentage: allocation.percentage,
        })),
        signatureDocument: parseStructuredDocument(editing.signatureDocument),
        signatureImageUrl: editing.signatureImageUrl || null,
        signatureImageWidth: editing.signatureImageWidth || 320,
        signatureImageLinkUrl: editing.signatureImageLinkUrl || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [editing, workflows]);

  const closeEditor = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    setBusy(true);
    try {
      const data = { ...form };
      const allocations = data.allocations;
      delete data.allocations;
      const signatureImage = data.signatureImage;
      const removeSignatureImage = data.removeSignatureImage;
      delete data.signatureImage;
      delete data.signatureImageUrl;
      delete data.removeSignatureImage;
      if (editing && !data.smtpUsername) delete data.smtpUsername;
      if (editing && !data.smtpPassword) delete data.smtpPassword;
      const result = await api.saveSender(
        editing?.id || null,
        data,
        allocations.map((allocation) => ({
          workflowId: allocation.workflow.id,
          percentage: Number(allocation.percentage),
        })),
      );
      if (!result.success) {
        enqueueSnackbar(result.errors.map((item) => item.message).join(" "), {
          variant: "error",
        });
        return;
      }
      if (signatureImage || removeSignatureImage) {
        setSignatureImageUpdating(true);
        const imageResult = await api.updateSenderSignatureImage(
          result.sender.id,
          signatureImage,
          removeSignatureImage,
        );
        if (!imageResult.success) {
          enqueueSnackbar(
            imageResult.errors.map((item) => item.message).join(" "),
            { variant: "error" },
          );
          return;
        }
        setForm((current) => ({
          ...current,
          signatureImageUrl: imageResult.sender?.signatureImageUrl || null,
          signatureImage: null,
          removeSignatureImage: false,
        }));
      }
      closeEditor();
      enqueueSnackbar("Sending profile saved.", { variant: "success" });
      await onChanged();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setSignatureImageUpdating(false);
      setBusy(false);
    }
  };

  const remove = async (sender) => {
    setBusy(true);
    try {
      const result = await api.deleteSender(sender.id);
      enqueueSnackbar(result.message, {
        variant: result.success ? "success" : "error",
      });
      if (result.success) await onChanged();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
      setSenderToDelete(null);
    }
  };

  const sendTest = async () => {
    if (!/^\S+@\S+\.\S+$/.test(recipient)) {
      enqueueSnackbar("Enter a valid internal test email address.", {
        variant: "error",
      });
      return;
    }
    setBusy(true);
    try {
      const result = await api.testSender(testSender.id, recipient);
      if (result.success) {
        enqueueSnackbar(result.message, { variant: "success" });
        setTestSender(null);
        setRecipient("");
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h5">Sending profiles</Typography>
          <Typography color="text.secondary">
            Manage the mailboxes used to send workflow emails.
          </Typography>
        </Box>
        <Button startIcon={<Add />} onClick={() => setEditing(emptyForm)}>
          Add sending profile
        </Button>
      </Stack>
      <Stack spacing={1} mt={2}>
        {senders.length === 0 ? (
          <Typography color="text.secondary">
            No sending profiles have been configured.
          </Typography>
        ) : (
          senders.map((sender) => (
            <Stack
              key={sender.id}
              direction={{ xs: "column", md: "row" }}
              alignItems={{ md: "center" }}
              spacing={2}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box flex={1}>
                <Typography fontWeight={700}>{sender.name}</Typography>
                <Typography variant="body2">
                  {sender.displayName} &lt;{sender.fromEmail}&gt;
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Replies to {sender.replyToEmail}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Today: {sender.todayConsumed} sent, {sender.todayReserved}{" "}
                  reserved, {sender.todayRemaining} remaining of{" "}
                  {sender.dailySendLimit}
                </Typography>
              </Box>
              <Chip
                size="small"
                color={sender.isActive ? "success" : "default"}
                label={sender.isActive ? "Active" : "Inactive"}
              />
              <Chip
                size="small"
                color={sender.credentialsConfigured ? "success" : "warning"}
                variant="outlined"
                label={
                  sender.credentialsConfigured
                    ? "Credentials configured"
                    : "Credentials required"
                }
              />
              <Stack direction="row">
                <Tooltip title="Test sending profile">
                  <span>
                    <IconButton
                      aria-label={`Test ${sender.name}`}
                      sx={{ border: "none" }}
                      disabled={
                        busy ||
                        !sender.isActive ||
                        !sender.credentialsConfigured
                      }
                      onClick={() => {
                        setTestSender(sender);
                      }}
                    >
                      <Send />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit sending profile">
                  <IconButton
                    aria-label={`Edit ${sender.name}`}
                    sx={{ border: "none" }}
                    disabled={busy}
                    onClick={() => setEditing(sender)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete sending profile">
                  <IconButton
                    aria-label={`Delete ${sender.name}`}
                    color="error"
                    sx={{ border: "none" }}
                    disabled={busy}
                    onClick={() => setSenderToDelete(sender)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          ))
        )}
      </Stack>

      <Dialog
        open={Boolean(editing)}
        onClose={closeEditor}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing?.id ? "Edit sending profile" : "Add sending profile"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Internal profile name"
              value={form.name}
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
              required
            />
            <TextField
              label="Display name"
              value={form.displayName}
              onChange={(event) =>
                setForm({ ...form, displayName: event.target.value })
              }
              required
            />
            <TextField
              label="From email"
              type="email"
              value={form.fromEmail}
              onChange={(event) =>
                setForm({ ...form, fromEmail: event.target.value })
              }
              required
            />
            <TextField
              label="Reply-to email"
              type="email"
              value={form.replyToEmail}
              onChange={(event) =>
                setForm({ ...form, replyToEmail: event.target.value })
              }
              required
            />
            <TextField
              label="Daily workflow email limit"
              type="number"
              value={form.dailySendLimit}
              onChange={(event) =>
                setForm({ ...form, dailySendLimit: Number(event.target.value) })
              }
              inputProps={{ min: 1 }}
              helperText="Maximum workflow emails this mailbox can attempt per calendar day."
              required
            />
            <Alert severity="info">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                Setting up a Google mailbox
              </Typography>
              <Box component="ol" sx={{ my: 0, pl: 2.5 }}>
                <li>Enable 2-Step Verification on the Google account.</li>
                <li>
                  Open Google&apos;s{" "}
                  <Link
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    App passwords
                  </Link>{" "}
                  page and create one for Fruitify.
                </li>
                <li>
                  Enter the full mailbox email address as the username and paste
                  the generated app password below.
                </li>
              </Box>
              <Typography variant="caption" component="p" mt={1}>
                Do not enter the mailbox&apos;s normal password. If App
                passwords is unavailable, your Google Workspace administrator
                may need to allow it.
              </Typography>
            </Alert>
            <TextField
              label="Google mailbox username"
              value={form.smtpUsername}
              onChange={(event) =>
                setForm({ ...form, smtpUsername: event.target.value })
              }
              helperText={
                editing?.id ? "Leave blank to keep the existing username." : ""
              }
              required={!editing?.id}
            />
            <TextField
              label="Google app password"
              type="password"
              value={form.smtpPassword}
              onChange={(event) =>
                setForm({ ...form, smtpPassword: event.target.value })
              }
              helperText={
                editing?.id ? "Leave blank to keep the existing password." : ""
              }
              required={!editing?.id}
            />
            <FormControlLabel
              label="Active"
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm({ ...form, isActive: event.target.checked })
                  }
                />
              }
            />
            <Box>
              <Autocomplete
                multiple
                options={workflows || []}
                value={form.allocations.map(
                  (allocation) => allocation.workflow,
                )}
                getOptionLabel={(option) =>
                  `${option.name} (${option.workflowTypeLabel})`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, selectedWorkflows) => {
                  const base = selectedWorkflows.length
                    ? Math.floor(100 / selectedWorkflows.length)
                    : 0;
                  const remainder = selectedWorkflows.length
                    ? 100 % selectedWorkflows.length
                    : 0;
                  setForm((current) => ({
                    ...current,
                    allocations: selectedWorkflows.map((workflow, index) => ({
                      workflow,
                      percentage: base + (index < remainder ? 1 : 0),
                    })),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned workflows"
                    helperText="Capacity percentages must total 100%."
                  />
                )}
              />
              {form.allocations.map((allocation, index) => (
                <TextField
                  key={allocation.workflow.id}
                  fullWidth
                  sx={{ mt: 1 }}
                  label={`${allocation.workflow.name} allocation`}
                  type="number"
                  value={allocation.percentage}
                  InputProps={{ endAdornment: "%" }}
                  inputProps={{ min: 1, max: 100 }}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      allocations: current.allocations.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, percentage: Number(event.target.value) }
                          : item,
                      ),
                    }))
                  }
                  disabled={form.allocations.length === 1}
                  helperText={`${Math.round(
                    (form.dailySendLimit * allocation.percentage) / 100,
                  )} prioritised emails per day`}
                />
              ))}
              {form.allocations.length > 1 && (
                <Typography
                  variant="caption"
                  color={
                    form.allocations.reduce(
                      (total, item) => total + Number(item.percentage || 0),
                      0,
                    ) === 100
                      ? "text.secondary"
                      : "error"
                  }
                >
                  Total allocation:{" "}
                  {form.allocations.reduce(
                    (total, item) => total + Number(item.percentage || 0),
                    0,
                  )}
                  %
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Email signature
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Type directly into the signature example and optionally add one
                image.
              </Typography>
              <SenderSignatureEditor
                key={editing?.id || "new-sender"}
                document={form.signatureDocument}
                onChange={(signatureDocument) =>
                  setForm((current) => ({ ...current, signatureDocument }))
                }
              >
                {(localSignatureImageUrl ||
                  (form.signatureImageUrl && !form.removeSignatureImage)) && (
                  <Box
                    component={form.signatureImageLinkUrl ? "a" : "span"}
                    href={form.signatureImageLinkUrl || undefined}
                    target={form.signatureImageLinkUrl ? "_blank" : undefined}
                    rel={form.signatureImageLinkUrl ? "noreferrer" : undefined}
                    sx={{ display: "block", width: "fit-content", mt: 1 }}
                  >
                    <Box
                      component="img"
                      src={localSignatureImageUrl || form.signatureImageUrl}
                      alt="Sender signature"
                      sx={{
                        maxWidth: "100%",
                        width: form.signatureImageWidth,
                        maxHeight: 160,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Box>
                )}
                {form.signatureImage && (
                  <Typography variant="caption" color="text.secondary">
                    New image ready to upload when the profile is saved.
                  </Typography>
                )}
                {(form.signatureImageUrl || form.signatureImage) && (
                  <Stack spacing={2} mt={2}>
                    <TextField
                      label="Signature image width (px)"
                      type="number"
                      size="small"
                      value={form.signatureImageWidth}
                      slotProps={{ htmlInput: { min: 80, max: 600, step: 10 } }}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          signatureImageWidth: Number(event.target.value),
                        })
                      }
                      helperText="Between 80 and 600 pixels"
                      sx={{ maxWidth: 240 }}
                    />
                    <TextField
                      label="Signature image link (optional)"
                      type="url"
                      value={form.signatureImageLinkUrl}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          signatureImageLinkUrl: event.target.value,
                        })
                      }
                      helperText="Recipients will open this address when they click the image."
                    />
                  </Stack>
                )}
                {signatureImageUpdating && (
                  <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">
                      Updating signature image…
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                  <Button component="label" variant="outlined">
                    {form.signatureImageUrl
                      ? "Replace signature image"
                      : "Upload signature image"}
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/gif"
                      onChange={(event) =>
                        setForm({
                          ...form,
                          signatureImage: event.target.files?.[0] || null,
                          removeSignatureImage: false,
                        })
                      }
                    />
                  </Button>
                  {(form.signatureImageUrl || form.signatureImage) && (
                    <Button
                      color="error"
                      onClick={() =>
                        setForm({
                          ...form,
                          signatureImage: null,
                          removeSignatureImage: Boolean(form.signatureImageUrl),
                        })
                      }
                    >
                      Remove signature image
                    </Button>
                  )}
                </Stack>
              </SenderSignatureEditor>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy}>
            Save profile
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(senderToDelete)}
        title="Delete sending profile?"
        message={`Delete “${senderToDelete?.name || ""}”? This cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setSenderToDelete(null)}
        onConfirm={() => remove(senderToDelete)}
      />

      <Dialog
        open={Boolean(testSender)}
        onClose={() => {
          if (!busy) setTestSender(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Test {testSender?.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Internal test recipient"
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            helperText="A simple connection-test email will be sent to this address."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestSender(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={sendTest}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : <Send />}
          >
            {busy ? "Sending..." : "Send test"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
